/**
 * Route configuration — single source of truth for the app's navigation structure.
 *
 * Used by:
 *  - BackButton / BackLink components to determine smart parent navigation
 *  - Breadcrumbs for label lookup (extends STATIC_LABELS)
 *  - Any component that needs to know "where does this route live?"
 */

export interface RouteConfig {
  /** URL path pattern (e.g. `/catalog/:slug`) */
  path: string
  /** Human-readable label (e.g. "Product Detail") */
  label: string
  /** The parent list route to go back to (e.g. `/catalog`) */
  parentRoute?: string
  /** Parent label (e.g. "Catalog") */
  parentLabel?: string
  /** Section group for contextual awareness */
  section: 'catalog' | 'contracts' | 'bundles' | 'learn' | 'checkout' | 'account' | 'legal' | 'other'
}

export const ROUTES: RouteConfig[] = [
  // Catalog section
  { path: '/', label: 'Home', section: 'other' },
  { path: '/catalog', label: 'Catalog', section: 'catalog' },
  { path: '/catalog/:slug', label: 'Product', section: 'catalog', parentRoute: '/catalog', parentLabel: 'Catalog' },
  { path: '/marketplace', label: 'Marketplace', section: 'catalog' },
  { path: '/marketplace/:slug', label: 'Product', section: 'catalog', parentRoute: '/marketplace', parentLabel: 'Marketplace' },

  // Contracts section
  { path: '/contracts', label: 'Contracts', section: 'contracts' },
  { path: '/contracts/:slug', label: 'Contract', section: 'contracts', parentRoute: '/contracts', parentLabel: 'Contracts' },

  // Bundles section
  { path: '/bundles', label: 'Bundles', section: 'bundles' },
  { path: '/bundles/:slug', label: 'Bundle', section: 'bundles', parentRoute: '/bundles', parentLabel: 'Bundles' },

  // Learn section
  { path: '/learn', label: 'Learn', section: 'learn' },
  { path: '/learn/:slug', label: 'Article', section: 'learn', parentRoute: '/learn', parentLabel: 'Learn' },

  // Checkout section
  { path: '/checkout/:itemType/:itemId', label: 'Checkout', section: 'checkout' },
  { path: '/checkout/success', label: 'Order Complete', section: 'checkout', parentRoute: '/profile', parentLabel: 'My Products' },

  // Account section
  { path: '/dashboard', label: 'Agent Dashboard', section: 'account' },
  { path: '/my-products', label: 'My Products', section: 'account' },
  { path: '/profile', label: 'Account', section: 'account' },
  { path: '/register', label: 'Register', section: 'account' },
  { path: '/pricing', label: 'Pricing', section: 'other' },
  { path: '/sell', label: 'Sell Your Code', section: 'other' },
  { path: '/seller-upload', label: 'Submit Code', section: 'other' },
  { path: '/seller-upload/status/:id', label: 'Submission Status', section: 'other', parentRoute: '/seller-upload', parentLabel: 'Submit Code' },
  { path: '/open-marketplace', label: 'Open Marketplace', section: 'catalog' },
  { path: '/vip', label: 'VIP API Docs', section: 'account' },

  // Legal section
  { path: '/terms', label: 'Terms of Service', section: 'legal' },
  { path: '/privacy', label: 'Privacy Policy', section: 'legal' },
  { path: '/refund', label: 'Refund Policy', section: 'legal' },
]

/** Static label map for breadcrumbs (extends the existing Breadcrumbs STATIC_LABELS) */
export const ROUTE_LABELS: Record<string, string> = {
  catalog: 'Catalog',
  marketplace: 'Marketplace',
  contracts: 'Contracts',
  bundles: 'Bundles',
  learn: 'Learn',
  dashboard: 'Agent Dashboard',
  'my-products': 'My Products',
  profile: 'Account',
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  refund: 'Refund Policy',
  checkout: 'Checkout',
  success: 'Order Complete',
  pricing: 'Pricing',
  sell: 'Sell Your Code',
  'seller-upload': 'Submit Code',
  'open-marketplace': 'Open Marketplace',
  vip: 'VIP API Docs',
}

/**
 * Given a pathname, find the matching route config and its parent.
 * Returns null if no match is found.
 */
export function matchRoute(pathname: string): RouteConfig | null {
  // Normalize pathname — strip query strings
  const path = pathname.split('?')[0]

  for (const route of ROUTES) {
    if (pathsMatch(route.path, path)) {
      return route
    }
  }
  return null
}

/**
 * Simple pattern matcher — handles `:param` segments.
 */
function pathsMatch(pattern: string, path: string): boolean {
  const patternParts = pattern.split('/').filter(Boolean)
  const pathParts = path.split('/').filter(Boolean)

  if (patternParts.length !== pathParts.length) return false

  for (let i = 0; i < patternParts.length; i++) {
    if (patternParts[i].startsWith(':')) continue
    if (patternParts[i] !== pathParts[i]) return false
  }

  return true
}
